// ============================================
// 이벤트 시스템 - 핵심 로직
// ============================================

import {
    GameEvent,
    GameEventOption,
    EventChain,
    ResourceState,
    Building,
    BuildingType,
    Character,
    CharacterId,
    Era,
    Season,
    EndingType
} from '../types';
import { DuckMBTI, MBTIModifier, updateMBTI, calculateMBTIType, DUCK_PERSONALITIES } from './duckMBTI';
import { ALL_EVENTS, CHAIN_DEFINITIONS } from './eventData';

// ============================================
// 이벤트 선택 시스템
// ============================================

export interface EventContext {
    resources: ResourceState;
    buildings: Record<BuildingType, Building>;
    characters: Record<CharacterId, Character>;
    activeChains: EventChain[];
    completedChains: string[];
    flags: Set<string>;
    triggeredEvents: Set<string>;
    mbti: DuckMBTI;
}

// 조건을 만족하는 이벤트 필터링
export function filterAvailableEvents(
    events: GameEvent[],
    context: EventContext
): GameEvent[] {
    return events.filter(event => {
        // 1회성 이벤트 체크
        if (event.oneTime && context.triggeredEvents.has(event.id)) {
            return false;
        }

        // 시대 조건
        if (event.minEra && getEraOrder(context.resources.era) < getEraOrder(event.minEra)) {
            return false;
        }
        if (event.maxEra && getEraOrder(context.resources.era) > getEraOrder(event.maxEra)) {
            return false;
        }

        // 인구 조건
        if (event.minPopulation && context.resources.population < event.minPopulation) {
            return false;
        }
        if (event.maxPopulation && context.resources.population > event.maxPopulation) {
            return false;
        }

        // 돈 조건
        if (event.minMoney && context.resources.money < event.minMoney) {
            return false;
        }
        if (event.maxMoney && context.resources.money > event.maxMoney) {
            return false;
        }

        // 행복도 조건
        if (event.minHappiness && context.resources.happiness < event.minHappiness) {
            return false;
        }
        if (event.maxHappiness && context.resources.happiness > event.maxHappiness) {
            return false;
        }

        // 계절 조건
        if (event.season && context.resources.season !== event.season) {
            return false;
        }

        // 플래그 조건
        if (event.requiresFlag && !context.flags.has(event.requiresFlag)) {
            return false;
        }

        // 건물 조건
        if (event.requiresBuilding) {
            const building = context.buildings[event.requiresBuilding];
            if (!building || building.count === 0) {
                return false;
            }
        }

        // 캐릭터 조건
        if (event.requiresCharacter) {
            const character = context.characters[event.requiresCharacter as CharacterId];
            if (!character || !character.isUnlocked) {
                return false;
            }
        }

        // 체인 이벤트 체크
        if (event.chainId && event.chainStep !== undefined) {
            const activeChain = context.activeChains.find(c => c.id === event.chainId);
            if (!activeChain || activeChain.currentStep !== event.chainStep) {
                return false;
            }
        }

        return true;
    });
}

// 가중치 기반 랜덤 선택
export function selectRandomEvent(
    events: GameEvent[],
    context: EventContext
): GameEvent | null {
    if (events.length === 0) return null;

    // 가중치 계산
    const weightedEvents = events.map(event => {
        let weight = event.weight || 10;

        // MBTI 보너스
        const mbtiType = calculateMBTIType(context.mbti);
        const personality = DUCK_PERSONALITIES[mbtiType];
        if (personality.bonusEffects.eventModifier.some(mod =>
            event.id.toLowerCase().includes(mod.replace('_bonus', ''))
        )) {
            weight *= 1.5;
        }

        // 활성 체인 이벤트 우선
        if (event.chainId) {
            const activeChain = context.activeChains.find(c => c.id === event.chainId);
            if (activeChain && activeChain.isActive) {
                weight *= 3;
            }
        }

        // 카테고리별 가중치 조정
        switch (event.category) {
            case 'crisis':
                // 행복도 낮으면 위기 이벤트 증가
                if (context.resources.happiness < 30) weight *= 2;
                break;
            case 'opportunity':
                // 행복도 높으면 기회 이벤트 증가
                if (context.resources.happiness > 70) weight *= 1.5;
                break;
            case 'milestone':
                weight *= 2; // 마일스톤은 우선
                break;
        }

        return { event, weight };
    });

    const totalWeight = weightedEvents.reduce((sum, e) => sum + e.weight, 0);
    let random = Math.random() * totalWeight;

    for (const { event, weight } of weightedEvents) {
        random -= weight;
        if (random <= 0) {
            return event;
        }
    }

    return events[0];
}

// ============================================
// 체인 이벤트 관리
// ============================================

export function startChain(chainId: string, turn: number): EventChain | null {
    const definition = CHAIN_DEFINITIONS[chainId];
    if (!definition) return null;

    return {
        id: chainId,
        name: definition.name,
        description: definition.description,
        totalSteps: definition.totalSteps,
        currentStep: 0,
        isActive: true,
        isCompleted: false,
        startedAt: turn,
        branches: []
    };
}

export function advanceChain(
    chain: EventChain,
    branchChoice?: string
): EventChain {
    const newStep = chain.currentStep + 1;
    const newBranches = branchChoice
        ? [...chain.branches, branchChoice]
        : chain.branches;

    return {
        ...chain,
        currentStep: newStep,
        isCompleted: newStep >= chain.totalSteps,
        isActive: newStep < chain.totalSteps,
        branches: newBranches
    };
}

export function getChainEvent(
    chainId: string,
    step: number,
    branches: string[]
): GameEvent | null {
    // 체인 이벤트 찾기
    const chainEvents = ALL_EVENTS.filter(
        e => e.chainId === chainId && e.chainStep === step
    );

    if (chainEvents.length === 0) return null;

    // 분기에 따른 이벤트 선택
    if (branches.length > 0 && chainEvents.length > 1) {
        const lastBranch = branches[branches.length - 1];
        const branchEvent = chainEvents.find(e =>
            e.id.includes(lastBranch)
        );
        if (branchEvent) return branchEvent;
    }

    return chainEvents[0];
}

// ============================================
// 시대 & 계절 시스템
// ============================================

export function getEraOrder(era: Era): number {
    const order: Record<Era, number> = {
        [Era.POND]: 0,
        [Era.VILLAGE]: 1,
        [Era.CITY]: 2,
        [Era.KINGDOM]: 3,
        [Era.EMPIRE]: 4
    };
    return order[era];
}

export function calculateEra(resources: ResourceState): Era {
    const { population, money } = resources;

    if (population >= 1000 && money >= 1000000) return Era.EMPIRE;
    if (population >= 500 && money >= 100000) return Era.KINGDOM;
    if (population >= 200 && money >= 10000) return Era.CITY;
    if (population >= 50 && money >= 1000) return Era.VILLAGE;
    return Era.POND;
}

export function advanceSeason(current: Season): Season {
    const order: Season[] = [Season.SPRING, Season.SUMMER, Season.AUTUMN, Season.WINTER];
    const idx = order.indexOf(current);
    return order[(idx + 1) % 4];
}

export function getSeasonEffects(season: Season): {
    happinessModifier: number;
    incomeModifier: number;
    eventModifier: string[];
} {
    switch (season) {
        case Season.SPRING:
            return {
                happinessModifier: 1.1,
                incomeModifier: 1.0,
                eventModifier: ['birth', 'growth', 'rain']
            };
        case Season.SUMMER:
            return {
                happinessModifier: 1.2,
                incomeModifier: 1.2,
                eventModifier: ['festival', 'heat', 'drought']
            };
        case Season.AUTUMN:
            return {
                happinessModifier: 1.0,
                incomeModifier: 1.3,
                eventModifier: ['harvest', 'migration', 'storm']
            };
        case Season.WINTER:
            return {
                happinessModifier: 0.8,
                incomeModifier: 0.7,
                eventModifier: ['cold', 'scarcity', 'cozy']
            };
    }
}

// ============================================
// 엔딩 체크
// ============================================

export function checkEndings(context: EventContext): EndingType | null {
    const { resources, buildings, characters, completedChains, flags } = context;

    // 부정적 엔딩 (우선 체크)
    if (resources.population <= 0) {
        return EndingType.EXTINCTION;
    }
    if (resources.happiness <= 0) {
        return EndingType.REBELLION;
    }

    // 체인 기반 엔딩 (밸런스 조절: 체인만 완료해도 엔딩)
    if (completedChains.includes('fox_invasion') && flags.has('fox_victory')) {
        return EndingType.FOX_CONQUEST;
    }
    if (completedChains.includes('fox_invasion') && flags.has('fox_alliance')) {
        return EndingType.FOX_ALLIANCE;
    }
    // 하늘 왕국: 체인 완료 또는 ascended 플래그
    if (completedChains.includes('sky_migration') || flags.has('ascended')) {
        return EndingType.SKY_KINGDOM;
    }
    // 바다 왕국: 체인 완료 또는 sea_kingdom 플래그
    if (completedChains.includes('sea_expedition') || flags.has('sea_kingdom')) {
        return EndingType.SEA_KINGDOM;
    }

    // 승천 엔딩 (백조 만남 + 행복도 80 이상)
    if (flags.has('swan_blessing') || (characters.mystic_swan?.isUnlocked && resources.happiness >= 80)) {
        return EndingType.ASCENSION;
    }

    // 대이주 엔딩 (이주 플래그)
    if (flags.has('migration_started') || flags.has('great_migration')) {
        return EndingType.MIGRATION;
    }

    // 조건 기반 엔딩 (밸런스 조절: 조건 완화)
    // 대륙의 지배자: 인구 1000 + 돈 500만 (기존: 2000 + 1000만)
    if (resources.population >= 1000 && resources.money >= 5000000) {
        return EndingType.CONQUEST;
    }

    // 모든 건물 최대 (기존 그대로)
    const allMaxed = Object.values(buildings).every(b => b.count >= b.maxCount);
    if (allMaxed) {
        return EndingType.GOLDEN_DUCK;
    }

    // 평화 엔딩 (밸런스 조절: 행복도 85+ 200턴 유지로 완화)
    if (flags.has('peace_500_turns') || flags.has('peace_200_turns') ||
        (resources.happiness >= 85 && resources.turn >= 200)) {
        return EndingType.PEACE;
    }

    // 전설 엔딩 (밸런스 조절: 5개 체인 + 1000턴으로 완화)
    if (completedChains.length >= 5 && resources.turn >= 1000) {
        return EndingType.LEGEND;
    }

    // 상인 제국 (시장 최대 + 항구 최대 + 돈 5천만)
    const marketCount = buildings[BuildingType.MARKET]?.count || 0;
    const harborCount = buildings[BuildingType.HARBOR]?.count || 0;
    if (marketCount >= 15 && harborCount >= 5 && resources.money >= 50000000) {
        return EndingType.MERCHANT_EMPIRE;
    }

    // 숨겨진 엔딩들
    if (flags.has('secret_duck_found') && completedChains.includes('secret_duck')) {
        return EndingType.SECRET_DUCK;
    }
    if (flags.has('time_paradox')) {
        return EndingType.TIME_LOOP;
    }

    // 진엔딩 (밸런스 조절: 캐릭터 5명 이상 만남 + 체인 3개 이상 완료)
    const unlockedCharacters = Object.values(characters).filter(c => c.isUnlocked).length;
    if (unlockedCharacters >= 5 && completedChains.length >= 3 && flags.has('enlightened')) {
        return EndingType.TRUE_ENDING;
    }
    // 또는 모든 캐릭터 만남
    const allCharactersMet = Object.values(characters).every(c => c.isUnlocked);
    if (allCharactersMet && completedChains.includes('true_path')) {
        return EndingType.TRUE_ENDING;
    }

    return null;
}

// ============================================
// 이벤트 결과 처리
// ============================================

export interface EventResult {
    resourceChanges: Partial<ResourceState>;
    mbtiChanges: MBTIModifier;
    newFlags: string[];
    unlockedCharacters: CharacterId[];
    chainUpdates: {
        start?: string;
        advance?: string;
        end?: string;
        branch?: string;
    };
    triggeredEnding?: EndingType;
    logMessage: string;
    logType: 'info' | 'success' | 'danger' | 'warning' | 'special';
}

export function processEventOption(
    option: GameEventOption,
    context: EventContext
): EventResult {
    const result: EventResult = {
        resourceChanges: {},
        mbtiChanges: {},
        newFlags: [],
        unlockedCharacters: [],
        chainUpdates: {},
        logMessage: option.outcomeDescription,
        logType: option.moneyChange >= 0 && option.happinessChange >= 0 ? 'success' : 'warning'
    };

    // 자원 변화
    if (option.moneyChange !== 0) {
        result.resourceChanges.money = option.moneyChange;
    }
    if (option.popChange !== 0) {
        result.resourceChanges.population = option.popChange;
    }
    if (option.happinessChange !== 0) {
        result.resourceChanges.happiness = option.happinessChange;
    }

    // MBTI 변화 추론 (선택지 텍스트 기반)
    result.mbtiChanges = inferMBTIFromChoice(option);

    // 플래그 설정
    if (option.setFlag) {
        result.newFlags.push(option.setFlag);
    }

    // 캐릭터 해금
    if (option.unlockCharacter) {
        result.unlockedCharacters.push(option.unlockCharacter as CharacterId);
    }

    // 체인 업데이트
    if (option.triggersChain) {
        result.chainUpdates.start = option.triggersChain;
    }
    if (option.continuesChain) {
        result.chainUpdates.advance = option.continuesChain;
    }
    if (option.endsChain) {
        result.chainUpdates.end = 'current';
    }

    // 엔딩 트리거
    if (option.triggersEnding) {
        result.triggeredEnding = option.triggersEnding;
        result.logType = 'special';
    }

    // 위험한 결과
    if (option.popChange < -10 || option.happinessChange < -20) {
        result.logType = 'danger';
    }

    return result;
}

// 선택지에서 MBTI 변화 추론 (변화량 증가: 5 -> 15)
function inferMBTIFromChoice(option: GameEventOption): MBTIModifier {
    const text = option.text.toLowerCase();
    const modifier: MBTIModifier = {};

    // E/I 추론 (외향/내향)
    if (text.includes('협상') || text.includes('대화') || text.includes('만나') ||
        text.includes('파티') || text.includes('축제') || text.includes('함께') ||
        text.includes('외교') || text.includes('환영') || text.includes('초대')) {
        modifier.E = 15;
    } else if (text.includes('혼자') || text.includes('거절') || text.includes('피하') ||
        text.includes('숨') || text.includes('조용') || text.includes('물러') ||
        text.includes('철수') || text.includes('기다')) {
        modifier.E = -15;
    }

    // S/N 추론 (감각/직관)
    if (text.includes('현실') || text.includes('안전') || text.includes('확실') ||
        text.includes('전통') || text.includes('검증') || text.includes('경험') ||
        text.includes('실용') || text.includes('신중') || text.includes('방어')) {
        modifier.S = 15;
    } else if (text.includes('모험') || text.includes('탐험') || text.includes('가능성') ||
        text.includes('상상') || text.includes('꿈') || text.includes('새로') ||
        text.includes('도전') || text.includes('미지') || text.includes('신비')) {
        modifier.S = -15;
    }

    // T/F 추론 (사고/감정)
    if (text.includes('효율') || text.includes('논리') || text.includes('분석') ||
        text.includes('계산') || text.includes('이익') || text.includes('처벌') ||
        text.includes('공정') || text.includes('법') || text.includes('합리')) {
        modifier.T = 15;
    } else if (text.includes('용서') || text.includes('도움') || text.includes('친절') ||
        text.includes('마음') || text.includes('감정') || text.includes('자비') ||
        text.includes('사랑') || text.includes('배려') || text.includes('희생')) {
        modifier.T = -15;
    }

    // J/P 추론 (판단/인식)
    if (text.includes('계획') || text.includes('준비') || text.includes('규칙') ||
        text.includes('정리') || text.includes('결정') || text.includes('체계') ||
        text.includes('질서') || text.includes('예정') || text.includes('확정')) {
        modifier.J = 15;
    } else if (text.includes('즉흥') || text.includes('자유') || text.includes('유연') ||
        text.includes('기회') || text.includes('변화') || text.includes('적응') ||
        text.includes('놀') || text.includes('재미') || text.includes('상황')) {
        modifier.J = -15;
    }

    return modifier;
}

// ============================================
// 유틸리티
// ============================================

export function generateEventId(): string {
    return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getEraName(era: Era): string {
    const names: Record<Era, string> = {
        [Era.POND]: '작은 연못',
        [Era.VILLAGE]: '오리 마을',
        [Era.CITY]: '오링 도시',
        [Era.KINGDOM]: '오링 왕국',
        [Era.EMPIRE]: '오링 제국'
    };
    return names[era];
}

export function getSeasonName(season: Season): string {
    const names: Record<Season, string> = {
        [Season.SPRING]: '봄',
        [Season.SUMMER]: '여름',
        [Season.AUTUMN]: '가을',
        [Season.WINTER]: '겨울'
    };
    return names[season];
}
